import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff } from "lucide-react";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { organisationUserRoles } from "@shared/schema";

interface CreateUserModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  isGod?: boolean;
  ownerOrgIds?: number[];
  ownerOrgs?: { id: number; name: string }[];
}

async function createUser({ username, password, organisationId, role }: { username: string; password: string; organisationId?: number, role?: string }) {
  const payload: any = { username, password };
  if (organisationId) payload.organisationId = organisationId;
  if (role) payload.role = role;
  const response = await fetch('/api/users', {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || "Failed to create user - its probably a duplicate username");
  }

  return response.json();
}

export function CreateUserModal({ isOpen, onOpenChange, isGod = false, ownerOrgIds = [], ownerOrgs = [] }: CreateUserModalProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>("operator");
  const queryClient = useQueryClient();
  const { toast } = useToast();

  async function assignUserToOrganisation({ organisationId, userId }: { organisationId: number; userId: number; }) {
    const response = await fetch(`/api/organisations/${organisationId}/users`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, role: "member" }),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Failed to assign user to organisation");
    }
    return response.json();
  }

  const mutation = useMutation({
    mutationFn: createUser,
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({ queryKey: ["organisations"] });
      toast({
        title: "User created",
        description: `User ${username} has been created.`,
      });
      onOpenChange(false);
      setUsername("");
      setPassword("");
      setSelectedOrgId(null);
      setSelectedRole("operator");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password) {
      toast({
        title: "Missing fields",
        description: "Please provide both username and password.",
        variant: "destructive",
      });
      return;
    }
    if (!isGod && (!selectedOrgId || ownerOrgs.length === 0)) {
      toast({ title: "Missing organisation", description: "Select an organisation.", variant: "destructive" });
      return;
    }
    if (isGod) {
      mutation.mutate({ username, password });
    } else {
      mutation.mutate({ username, password, organisationId: selectedOrgId ?? undefined, role: selectedRole });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create New User</DialogTitle>
          <DialogDescription>
            {isGod
              ? "Create a new user account. This user will not be assigned to any organisation."
              : "Create a new user and assign them to one of your organisations."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="username" className="text-right">
                Username
              </Label>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="col-span-3"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="password" className="text-right">
                Password
              </Label>
              <div className="col-span-3 relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  <span className="sr-only">{showPassword ? "Hide password" : "Show password"}</span>
                </Button>
              </div>
            </div>
            {!isGod && ownerOrgs.length > 0 && (
              <>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="org" className="text-right">Organisation</Label>
                <div className="col-span-3">
                  <Select value={selectedOrgId ? String(selectedOrgId) : ''} onValueChange={val => setSelectedOrgId(Number(val))}>
                    <SelectTrigger id="org">
                      <SelectValue placeholder="Select organisation" />
                    </SelectTrigger>
                    <SelectContent>
                      {ownerOrgs.map(org => (
                        <SelectItem key={org.id} value={String(org.id)}>{org.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="role" className="text-right">Role</Label>
                <div className="col-span-3">
                  <Select value={selectedRole} onValueChange={setSelectedRole}>
                    <SelectTrigger id="role">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="operator">Operator</SelectItem>
                      <SelectItem value="owner">Owner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? "Creating..." : "Create User"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
} 